import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isSameDayBookingAllowed, getDefaultStartDate } from '../utils/DateValidation';

const PURPLE = '#5b1dee';
const PURPLE_LIGHT = 'rgba(91, 29, 238, 0.18)';

export const MobileCalendar = ({ 
  selectedDates, 
  onDateSelect, 
  onApply, 
  onClose,
  minDate = new Date(),
  title = "When's your trip?"
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('dates'); // 'dates' or 'months'
  const [selectedStartDate, setSelectedStartDate] = useState(selectedDates?.startDate || getDefaultStartDate());
  const [selectedEndDate, setSelectedEndDate] = useState(selectedDates?.endDate || getDefaultStartDate());
  const [selectionMode, setSelectionMode] = useState('start'); // 'start' or 'end'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Adjust for Monday as first day of week (0 = Monday, 6 = Sunday)
    const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    return { daysInMonth, startingDayOfWeek: adjustedStartingDay };
  };

  const getPreviousMonth = () => {
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    if (prevMonth >= minDate) {
      setCurrentDate(prevMonth);
    }
  };

  const getNextMonth = () => {
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentDate(nextMonth);
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    if (selectionMode === 'start') {
      setSelectedStartDate(clickedDate);
      // If end date is before start date, set end date to start date
      if (clickedDate > selectedEndDate) {
        setSelectedEndDate(clickedDate);
      }
      setSelectionMode('end');
    } else {
      if (clickedDate >= selectedStartDate) {
        setSelectedEndDate(clickedDate);
        setSelectionMode('start');
      } else {
        // If clicked date is before start date, make it the new start date
        setSelectedStartDate(clickedDate);
        setSelectedEndDate(clickedDate);
        setSelectionMode('end');
      }
    }
  };

  const handleApply = () => {
    if (onDateSelect) {
      onDateSelect({
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
    }
    if (onApply) {
      onApply();
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const newDate = new Date(selectedYear, month, 1);
    setCurrentDate(newDate);
    setViewMode('dates');
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    const newDate = new Date(year, selectedMonth, 1);
    setCurrentDate(newDate);
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isDateSelected = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const isStartDate = selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
    
    const isEndDate = selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
    
    const isInRange = selectedStartDate && selectedEndDate &&
           dateToCheck >= selectedStartDate && dateToCheck <= selectedEndDate;
    
    return isStartDate || isEndDate || isInRange;
  };

  const isDateStart = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedStartDate && 
           dateToCheck.getDate() === selectedStartDate.getDate() &&
           dateToCheck.getMonth() === selectedStartDate.getMonth() &&
           dateToCheck.getFullYear() === selectedStartDate.getFullYear();
  };

  const isDateEnd = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return selectedEndDate && 
           dateToCheck.getDate() === selectedEndDate.getDate() &&
           dateToCheck.getMonth() === selectedEndDate.getMonth() &&
           dateToCheck.getFullYear() === selectedEndDate.getFullYear();
  };

  const isDateDisabled = (day) => {
    const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return dateToCheck < minDate || !isSameDayBookingAllowed(dateToCheck);
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);

  // Get previous month's days to fill the grid
  const getPreviousMonthDays = () => {
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    const days = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(daysInPrevMonth - i);
    }
    return days;
  };

  const previousMonthDays = getPreviousMonthDays();

  return (
    <div className="fixed inset-0 bg-white z-[999999999] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="w-10"></div>
      </div>

      {/* Toggle between Dates and Months */}
      <div className="p-4">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('dates')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'dates' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span>Dates</span>
              {viewMode === 'dates' && (
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              )}
            </div>
          </button>
          <button
            onClick={() => setViewMode('months')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'months' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600'
            }`}
          >
            Months
          </button>
        </div>
      </div>

             {/* Calendar Navigation */}
       <div className="flex items-center justify-between px-4 mb-4">
         <button
           onClick={getPreviousMonth}
           className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
           disabled={new Date(currentDate.getFullYear(), currentDate.getMonth() - 1) < minDate}
         >
           <ChevronLeft className="w-5 h-5" />
         </button>
         <h3 className="text-lg font-semibold">{formatMonthYear(currentDate)}</h3>
         <button
           onClick={getNextMonth}
           className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
         >
           <ChevronRight className="w-5 h-5" />
         </button>
       </div>

       {/* Selection Mode Indicator */}
       <div className="px-4 mb-4">
         <div className="text-sm text-[#666666] text-center">
           {viewMode === 'dates' 
             ? (selectionMode === 'start' ? 'Select check-in date' : 'Select check-out date')
             : 'Select month and year'
           }
         </div>
       </div>

       {/* Content based on view mode */}
       {viewMode === 'dates' ? (
         /* Calendar Grid */
         <div className="flex-1 px-4 pb-24">
           {/* Days of week header */}
           <div className="grid grid-cols-7 gap-1 mb-2">
             {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((day) => (
               <div key={day} className="text-center text-sm text-gray-500 py-2">
                 {day}
               </div>
             ))}
           </div>

           {/* Calendar grid */}
           <div className="grid grid-cols-7 gap-1">
             {/* Previous month days */}
             {previousMonthDays.map((day) => (
               <div key={`prev-${day}`} className="text-center py-2 text-gray-300">
                 {day}
               </div>
             ))}

             {/* Current month days */}
             {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
               const isSelected = isDateSelected(day);
               const isStart = isDateStart(day);
               const isEnd = isDateEnd(day);
               const isDisabled = isDateDisabled(day);
               const isSingleDay = isStart && isEnd;

               return (
                 <button
                   key={day}
                   onClick={() => !isDisabled && handleDateClick(day)}
                   disabled={isDisabled}
                   className="relative text-center py-2 text-sm leading-tight transition-colors touch-manipulation flex items-center justify-center min-h-[36px]"
                   style={{
                     backgroundColor: isSelected || isStart || isEnd ? PURPLE_LIGHT : undefined,
                     borderRadius: isSingleDay
                       ? '50%'
                       : isStart
                       ? '50% 0 0 50%'
                       : isEnd
                       ? '0 50% 50% 0'
                       : isSelected
                       ? '0'
                       : undefined,
                   }}
                 >
                   {isStart || isEnd ? (
                     <span
                       className="relative z-10 inline-flex items-center justify-center w-[32px] h-[32px] rounded-full text-white font-medium text-sm"
                       style={{ backgroundColor: PURPLE }}
                     >
                       {day}
                     </span>
                   ) : (
                     <span
                       className={[
                         isSelected ? 'text-gray-900 font-medium' : '',
                         isDisabled ? 'text-gray-300' : !isSelected ? 'text-gray-900' : '',
                       ].join(' ')}
                     >
                       {day}
                     </span>
                   )}
                 </button>
               );
             })}

             {/* Next month days to fill the grid */}
             {(() => {
               const totalCells = 42; // 6 rows * 7 columns
               const usedCells = previousMonthDays.length + daysInMonth;
               const remainingCells = Math.max(0, totalCells - usedCells);
               
               return Array.from({ length: remainingCells }, (_, i) => (
                 <div key={`next-${i + 1}`} className="text-center py-2 text-gray-300">
                   {i + 1}
                 </div>
               ));
             })()}
           </div>
         </div>
       ) : (
         /* Months and Years Selection */
         <div className="flex-1 px-4 pb-24">
           {/* Year Selection */}
           <div className="mb-6">
             <h4 className="text-lg font-semibold mb-3">Year</h4>
             <div className="grid grid-cols-3 gap-2">
               {Array.from({ length: 5 }, (_, i) => selectedYear - 2 + i).map((year) => (
                 <button
                   key={year}
                   onClick={() => handleYearSelect(year)}
                   className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                     year === selectedYear
                       ? 'bg-[#5b1dee] text-white'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                   }`}
                 >
                   {year}
                 </button>
               ))}
             </div>
           </div>

           {/* Month Selection */}
           <div>
             <h4 className="text-lg font-semibold mb-3">Month</h4>
             <div className="grid grid-cols-3 gap-2">
               {[
                 'January', 'February', 'March',
                 'April', 'May', 'June',
                 'July', 'August', 'September',
                 'October', 'November', 'December'
               ].map((month, index) => (
                 <button
                   key={month}
                   onClick={() => handleMonthSelect(index)}
                   className={`py-3 px-4 rounded-lg text-center font-medium transition-colors ${
                     index === selectedMonth && selectedYear === currentDate.getFullYear()
                       ? 'bg-[#5b1dee] text-white'
                       : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                   }`}
                 >
                   {month}
                 </button>
               ))}
             </div>
           </div>
         </div>
       )}

      {/* Apply Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <button
          onClick={handleApply}
          className="w-full bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee] text-white py-4 px-6 rounded-[12px] font-semibold text-lg shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] hover:shadow-[-1px_6px_10px_0_rgba(93,0,207,0.5)] hover:translate-y-[-3px] active:bg-[#5b1dee] touch-manipulation flex items-center justify-center gap-2 transition-all duration-200"
        >
          Apply
        </button>
      </div>
    </div>
  );
};
